import { useState } from "react";
import { PhoneField, PHONE_COUNTRIES, parsePhone as parsePhoneShared, serializePhone } from "../PhoneField";
import { updateProfile, clearProfileCache } from "../../userProfile";

/* ============================================================================
 * ShipperUserProfile — editable view of the shipper's OWN user_profiles row.
 * Lets them update first_name / last_name / phone / email. The DB trigger
 * enforce_profile_update() blocks anything privileged (role / supplier_id
 * / is_active) so the API would reject those even if we sent them.
 *
 * Email is stored on user_profiles.email but the actual sign-in email lives
 * on auth.users.email. We surface a note explaining this — Supabase's email
 * change requires a verification flow we don't want to scope here.
 * ============================================================================ */

const T = {
  en: {
    title: "My Profile",
    sub: "Update your personal contact details. Changes save immediately.",
    section_name: "Name",
    section_contact: "Contact",
    firstName: "First name", lastName: "Last name",
    phone: "Phone", email: "Email (display only)",
    emailNote: "Your sign-in email is managed by GFI. Contact us to change it.",
    save: "Save changes", saving: "Saving…", saved: "Profile updated.",
    err: "Could not save. ", req: "First name, last name and phone are required.",
  },
  es: {
    title: "Mi Perfil",
    sub: "Actualiza tus datos personales de contacto. Los cambios se guardan al instante.",
    section_name: "Nombre",
    section_contact: "Contacto",
    firstName: "Nombre", lastName: "Apellido",
    phone: "Teléfono", email: "Correo (sólo informativo)",
    emailNote: "Tu correo de acceso lo gestiona GFI. Contáctanos para cambiarlo.",
    save: "Guardar cambios", saving: "Guardando…", saved: "Perfil actualizado.",
    err: "No se pudo guardar. ", req: "Nombre, apellido y teléfono son obligatorios.",
  },
};

const inp = {
  padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)",
  borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)",
  outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box",
};

function dialToCountryCode(d) {
  const withPlus = "+" + String(d || "").replace(/\D/g, "");
  const hit = PHONE_COUNTRIES.find(function (c) { return c.dial === withPlus; });
  return hit ? hit.code : "MX";
}
function countryCodeToDialDigits(code) {
  const hit = PHONE_COUNTRIES.find(function (c) { return c.code === code; });
  return hit ? hit.dial.replace(/\D/g, "") : "52";
}

function Field({ label, sub, required, error, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)" }}>
        {label}{required && <span style={{ color: "var(--color-danger)", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {sub && !error && <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{sub}</span>}
      {error && <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{error}</span>}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: "16px 20px", marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </div>
  );
}

export default function ShipperUserProfile({ profile, lang }) {
  const t = T[lang === "en" ? "en" : "es"];
  const initial = profile || {};
  const [form, setForm] = useState({
    firstName: initial.first_name || "",
    lastName: initial.last_name || "",
    phoneCountry: initial.phone_country_code || "52",
    phoneArea: initial.phone_area_code || "",
    phoneNumber: initial.phone_number || "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const phoneSerialized = serializePhone(dialToCountryCode(form.phoneCountry || "52"), form.phoneArea, form.phoneNumber);
  const commitPhone = function (s) {
    const p = parsePhoneShared(s);
    setForm(function (prev) {
      return Object.assign({}, prev, {
        phoneCountry: countryCodeToDialDigits(p.country),
        phoneArea: p.area,
        phoneNumber: p.local,
      });
    });
  };

  const save = async function () {
    setMsg(""); setErr("");
    if (!form.firstName.trim() || !form.lastName.trim() || (!form.phoneArea && !form.phoneNumber)) {
      setErr(t.req);
      return;
    }
    setBusy(true);
    try {
      await updateProfile(initial.id, {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        phone_country_code: form.phoneCountry || null,
        phone_area_code: form.phoneArea || null,
        phone_number: form.phoneNumber || null,
      });
      clearProfileCache();
      setMsg(t.saved);
      setTimeout(function () { setMsg(""); }, 4000);
    } catch (e) {
      setErr(t.err + (e && e.message ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t.title}</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-muted)" }}>{t.sub}</p>
      </div>

      {err && (
        <div style={{ background: "rgba(231,76,60,0.10)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--color-danger)", fontWeight: 500, marginBottom: 14 }}>{err}</div>
      )}
      {msg && (
        <div style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#059669", fontWeight: 600, marginBottom: 14 }}>{msg}</div>
      )}

      <Section title={t.section_name}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label={t.firstName} required>
            <input type="text" value={form.firstName} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { firstName: e.target.value }); }); }} style={inp} />
          </Field>
          <Field label={t.lastName} required>
            <input type="text" value={form.lastName} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { lastName: e.target.value }); }); }} style={inp} />
          </Field>
        </div>
      </Section>

      <Section title={t.section_contact}>
        <Field label={t.phone} required>
          <PhoneField value={phoneSerialized} onChange={commitPhone} lang={lang} inputStyle={inp} />
        </Field>
        <Field label={t.email} sub={t.emailNote}>
          <input type="email" value={initial.email || ""} readOnly style={Object.assign({}, inp, { background: "var(--color-bg)", color: "var(--color-text-muted)" })} />
        </Field>
      </Section>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={save} disabled={busy} style={{ padding: "9px 18px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: busy ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
          {busy ? t.saving : t.save}
        </button>
      </div>
    </div>
  );
}
